import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useNavigate, useParams } from 'react-router-dom'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useAppDispatch, useAppSelector } from '../../shared/redux/hooks'
import {
    deleteTariffApi,
    exportTariffApi,
    getTariffHistoryApi,
    getTariffRateAnalysisApi,
} from '../../apis/apis'
import type { TariffHistoryItem, TariffRateAnalysisRow, TariffRateItem } from '../../apis/types'
import Modal from '../../components/Modal'
import Panel from '../../components/Panel'
import { pushToast } from '../../components/Toaster/Toaster.slice'
import QueryError from '../conductor/QueryError'
import Layout from '../programs/Layout'
import { clearTariffDetail, getTariff } from './Tariffs.slice'
import { LIST_SEARCH_KEY } from './Tariffs'
import { BackToTariffsLink, TariffPageHeader } from './components/TariffPageHeader'
import { formatTariffUtilityLabel, TariffDetailHeader } from './components/TariffDetailHeader'
import { TariffRatesTable } from './components/TariffRatesTable'
import { TariffUsagesPanel } from './components/TariffUsagesPanel'
import { TariffRatesetSection } from './components/TariffHistorySwitcher'
import { TariffRateDocuments } from './components/TariffRateDocuments'
import { CannotDeleteTariffModal } from './components/CannotDeleteTariffModal'
import { setNotesViewContext } from '../notes/notesViewContext'
import { EditTariffModal } from './components/EditTariffModal'
import { TariffActivityLogPanel } from './components/TariffActivityLogPanel'
import { tariffAuditPackageFileName } from './components/tariffDownloadNames'
import { TariffRateValidationTable } from './components/TariffRateValidationTable'

function apiErrorMessage(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message
        if (typeof message === 'string' && message.trim() !== '') {
            return message
        }
    }
    return fallback
}

const Tariff: React.FC = () => {
    const { tariffId } = useParams<{ tariffId: string }>()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { tariff, detailLoading, detailError } = useAppSelector((state) => state.tariffs)
    const { permissions } = useAppSelector((state) => state.app)
    const selectedHistoryRef = useRef<string | null>(null)
    const historyRequestRef = useRef(0)
    const prevTariffIdRef = useRef<string | undefined>()

    const [usagesExpanded, setUsagesExpanded] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [rates, setRates] = useState<TariffRateItem[]>([])
    const [usageCount, setUsageCount] = useState<number | null>(null)
    const [totalUsageCount, setTotalUsageCount] = useState<number | null>(null)
    const [editOpen, setEditOpen] = useState(false)
    const [selectedHistoryCuid, setSelectedHistoryCuid] = useState<string | null>(null)
    const [histories, setHistories] = useState<TariffHistoryItem[]>([])
    const [confirmModal, setConfirmModal] = useState<React.ReactElement | null>(null)
    const [analysis, setAnalysis] = useState<TariffRateAnalysisRow[]>([])
    const [analysisLoading, setAnalysisLoading] = useState(false)
    const [analysisError, setAnalysisError] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [activityLogKey, setActivityLogKey] = useState(0)

    const canEdit = permissions?.includes('tariffs:update:all') ?? false
    const isCurrent = tariff != null && tariff.cuid === tariffId

    selectedHistoryRef.current = selectedHistoryCuid

    const listSearch = sessionStorage.getItem(LIST_SEARCH_KEY)
    const backHref =
        listSearch != null && listSearch !== '' ? `/admin/tariffs?${listSearch}` : '/admin/tariffs'

    const refreshActivityLog = useCallback(() => {
        setActivityLogKey((key) => key + 1)
    }, [])

    const reload = useCallback(() => {
        if (tariffId == null) return
        const historyCuid = selectedHistoryRef.current
        void dispatch(
            historyCuid != null ? getTariff({ tariffId, historyCuid }) : getTariff(tariffId)
        )
    }, [dispatch, tariffId])

    const reloadAfterWrite = useCallback(() => {
        reload()
        refreshActivityLog()
    }, [reload, refreshActivityLog])

    useEffect(() => {
        reload()
        return () => {
            dispatch(clearTariffDetail())
        }
    }, [dispatch, reload])

    useEffect(() => {
        if (isCurrent && tariff != null) {
            const isNewTariff = prevTariffIdRef.current !== tariffId
            if (isNewTariff) {
                setUsageCount(null)
                setTotalUsageCount(null)
                setSelectedHistoryCuid(null)
            }
            setHistories(tariff.histories ?? [])
            const incomingHistory = tariff.selectedHistoryCuid ?? tariff.histories?.[0]?.cuid ?? null
            if (isNewTariff || selectedHistoryRef.current == null) {
                setSelectedHistoryCuid(incomingHistory)
                setRates(tariff.rates)
            } else if (tariff.selectedHistoryCuid === selectedHistoryRef.current) {
                setRates(tariff.rates)
            }
            if (tariff.activeUsageCount != null) {
                setUsageCount(tariff.activeUsageCount)
            }
            if (tariff.totalUsageCount != null) {
                setTotalUsageCount(tariff.totalUsageCount)
            }
            prevTariffIdRef.current = tariffId
            setAnalysisLoading(true)
            setAnalysisError(false)
            void getTariffRateAnalysisApi(tariff.cuid)
                .then(setAnalysis)
                .catch(() => {
                    setAnalysis([])
                    setAnalysisError(true)
                })
                .finally(() => setAnalysisLoading(false))
        }
    }, [isCurrent, tariff, tariffId])

    const handleSelectHistory = async (historyCuid: string): Promise<void> => {
        if (tariffId == null) return
        const previousHistoryCuid = selectedHistoryCuid
        const requestId = ++historyRequestRef.current
        try {
            const history = await getTariffHistoryApi(tariffId, historyCuid)
            if (requestId !== historyRequestRef.current) return
            setSelectedHistoryCuid(historyCuid)
            setRates(history.rates ?? [])
            setHistories((prev) =>
                prev.map((h) => (h.cuid === history.cuid ? { ...h, ...history } : h))
            )
        } catch {
            if (requestId !== historyRequestRef.current) return
            setSelectedHistoryCuid(previousHistoryCuid)
            void dispatch(pushToast({ type: 'error', message: 'Failed to load rateset' }))
        }
    }

    const handleExport = async (): Promise<void> => {
        if (tariffId == null || exporting) return
        setExporting(true)
        try {
            const blob = await exportTariffApi(tariffId, 'csv')
            const url = URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = url
            anchor.download = tariffAuditPackageFileName(
                tariff?.utilityShortName,
                tariff?.schedule ?? tariffId,
                tariff?.histories?.[0]?.effectiveDate
            )
            anchor.click()
            URL.revokeObjectURL(url)
        } catch {
            void dispatch(pushToast({ type: 'error', message: 'Failed to download audit package' }))
        } finally {
            setExporting(false)
        }
    }

    const handleDelete = async (): Promise<void> => {
        if (tariffId == null || !canEdit) return
        if ((totalUsageCount ?? 0) > 0) {
            setConfirmModal(
                <CannotDeleteTariffModal
                    tariffCuid={tariffId}
                    activeUsageCount={usageCount ?? 0}
                    onClose={() => setConfirmModal(null)}
                />
            )
            return
        }
        if (totalUsageCount == null) {
            void dispatch(
                pushToast({
                    type: 'error',
                    message: 'Usage count is still loading. Try delete again in a moment.',
                })
            )
            return
        }
        let reason = ''
        try {
            const confirmed = await new Promise<unknown>((resolve, reject) => {
                setConfirmModal(
                    <Modal
                        resolve={resolve}
                        reject={reject}
                        title="Delete this tariff?"
                        message="This permanently removes the tariff and its rates. This cannot be undone."
                        reasonRequired
                    />
                )
            })
            reason = typeof confirmed === 'string' ? confirmed.trim() : ''
        } catch {
            setConfirmModal(null)
            return
        }
        setConfirmModal(null)
        if (reason === '') return
        setDeleting(true)
        try {
            await deleteTariffApi(tariffId, reason)
            void dispatch(pushToast({ type: 'success', message: 'Tariff deleted' }))
            navigate(backHref)
        } catch (error) {
            void dispatch(
                pushToast({
                    type: 'error',
                    message: apiErrorMessage(
                        error,
                        'Failed to delete tariff. It may still have meter service usages.'
                    ),
                })
            )
        } finally {
            setDeleting(false)
        }
    }

    const selectedHistory = useMemo(
        () => histories.find((h) => h.cuid === selectedHistoryCuid) ?? histories[0] ?? null,
        [histories, selectedHistoryCuid]
    )

    const utilityLabel = formatTariffUtilityLabel(
        tariff?.utilityName,
        tariff?.utilityShortName,
        tariff?.utilityType
    )

    const tariffNotesLabel =
        tariff?.name != null && tariff.name !== ''
            ? `${tariff.schedule} · ${tariff.name}`
            : tariff?.schedule

    useEffect(() => {
        if (tariffId == null || tariff?.cuid !== tariffId) {
            setNotesViewContext(null)
            return
        }
        setNotesViewContext({
            sourceEntity: `tariff|${tariffId}`,
            label: tariffNotesLabel,
        })
        return () => setNotesViewContext(null)
    }, [tariffId, tariff?.cuid, tariffNotesLabel])

    const backLink = <BackToTariffsLink to={backHref} />

    const detailActions =
        isCurrent && tariff != null ? (
            canEdit ? (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setEditOpen(true)}
                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    >
                        Edit tariff
                    </button>
                    <button
                        type="button"
                        disabled={deleting || totalUsageCount == null}
                        onClick={() => void handleDelete()}
                        className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/40"
                    >
                        {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                </div>
            ) : (
                <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                    <InformationCircleIcon className="h-4 w-4 shrink-0" />
                    Sufficient permission required to edit tariffs.
                </span>
            )
        ) : null

    const showStickyDetailHeader = isCurrent && tariff != null

    return (
        <Layout
            contentClassName="bg-white dark:bg-slate-900"
            title={
                showStickyDetailHeader ? (
                    <></>
                ) : (
                    <TariffPageHeader reserveNotesFab title={backLink} />
                )
            }
        >
            {detailError && !isCurrent ? (
                <QueryError message="Failed to load tariff." onRetry={reload} />
            ) : null}

            {detailLoading && !isCurrent ? (
                <div className="animate-pulse space-y-4">
                    <div className="h-24 rounded-lg bg-slate-200 dark:bg-slate-700" />
                    <div className="h-40 rounded-lg bg-slate-200 dark:bg-slate-700" />
                </div>
            ) : null}

            {isCurrent && tariff != null ? (
                <div className="space-y-5">
                    <TariffDetailHeader
                        backLink={backLink}
                        tariff={tariff}
                        utilityLabel={utilityLabel}
                        detailActions={detailActions}
                        exportAction={
                            <button
                                type="button"
                                disabled={exporting}
                                aria-busy={exporting}
                                onClick={() => void handleExport()}
                                className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                            >
                                {exporting ? (
                                    <svg
                                        className="mr-2 h-4 w-4 animate-spin"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                ) : null}
                                Download audit package
                            </button>
                        }
                    />

                    <Panel header="Rates">
                        {histories.length > 0 ? (
                            <div className="-mx-4 -mt-3 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
                                <TariffRatesetSection
                                    histories={histories}
                                    selectedHistoryCuid={selectedHistoryCuid}
                                    tariffCuid={tariff.cuid}
                                    canEdit={canEdit}
                                    onSelect={(cuid) => void handleSelectHistory(cuid)}
                                    onChanged={reloadAfterWrite}
                                />
                            </div>
                        ) : null}
                        <div className={histories.length > 0 ? 'space-y-4 pt-4' : 'space-y-3'}>
                            {selectedHistory != null ? (
                                <section
                                    aria-label="Rate sheets"
                                    className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50"
                                >
                                    <TariffRateDocuments
                                        historyCuid={selectedHistory.cuid}
                                        utilityCuid={tariff?.utilityCuid ?? null}
                                        attachments={selectedHistory.attachments ?? []}
                                        canEdit={canEdit}
                                        onAttachmentsChange={(attachments) => {
                                            const historyCuid = selectedHistory.cuid
                                            setHistories((prev) =>
                                                prev.map((h) =>
                                                    h.cuid === historyCuid ? { ...h, attachments } : h
                                                )
                                            )
                                            refreshActivityLog()
                                        }}
                                    />
                                </section>
                            ) : null}
                            <TariffRatesTable
                                rates={rates}
                                canEdit={canEdit}
                                tariffCuid={tariff.cuid}
                                tariffHistoryCuid={selectedHistoryCuid}
                                historyEffectiveDate={selectedHistory?.effectiveDate}
                                historyEndDate={selectedHistory?.endDate}
                                historyStatus={selectedHistory?.status}
                                onRateUpdated={(updated) => {
                                    setRates((prev) =>
                                        prev.map((r) => (r.cuid === updated.cuid ? updated : r))
                                    )
                                    refreshActivityLog()
                                }}
                                onRateCreated={(created) => {
                                    setRates((prev) => [...prev, created])
                                    if (tariffId != null) {
                                        const historyCuid =
                                            created.tariffHistoryCuid ??
                                            selectedHistoryRef.current ??
                                            undefined
                                        void dispatch(
                                            historyCuid != null
                                                ? getTariff({ tariffId, historyCuid })
                                                : getTariff(tariffId)
                                        )
                                    }
                                    refreshActivityLog()
                                }}
                                onRateDeleted={(rateCuid) => {
                                    setRates((prev) => prev.filter((r) => r.cuid !== rateCuid))
                                    if (tariff != null) {
                                        setAnalysisLoading(true)
                                        void getTariffRateAnalysisApi(tariff.cuid)
                                            .then(setAnalysis)
                                            .catch(() => setAnalysis([]))
                                            .finally(() => setAnalysisLoading(false))
                                    }
                                    refreshActivityLog()
                                }}
                            />
                        </div>
                    </Panel>

                    <Panel header="Rate validation" collapsible>
                        {analysisError ? (
                            <QueryError
                                message="Failed to load rate validation."
                                onRetry={() => {
                                    if (tariff == null) return
                                    setAnalysisLoading(true)
                                    setAnalysisError(false)
                                    void getTariffRateAnalysisApi(tariff.cuid)
                                        .then(setAnalysis)
                                        .catch(() => {
                                            setAnalysis([])
                                            setAnalysisError(true)
                                        })
                                        .finally(() => setAnalysisLoading(false))
                                }}
                            />
                        ) : (
                            <TariffRateValidationTable
                                description="Generation, delivery, and NBC totals by season and TOU, compared to the previous rateset. Demand, service, and PDP charges are not included."
                                rows={analysis}
                                loading={analysisLoading}
                            />
                        )}
                    </Panel>

                    <TariffActivityLogPanel tariffId={tariff.cuid} refreshKey={activityLogKey} />

                    {usageCount != null ? (
                        <TariffUsagesPanel
                            tariffCuid={tariff.cuid}
                            activeUsageCount={usageCount}
                            expanded={usagesExpanded}
                            onExpandedChange={setUsagesExpanded}
                        />
                    ) : null}

                    <EditTariffModal
                        open={editOpen}
                        onClose={() => setEditOpen(false)}
                        tariff={tariff}
                        onSaved={reloadAfterWrite}
                    />
                </div>
            ) : null}
            {confirmModal}
        </Layout>
    )
}

export default Tariff
